import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getProviderAudit } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/**
 * Provider configuration audit trail (CT-4184). Returns the events plus a
 * reference `now` captured at load time (off-render, inside the fetch) so
 * relative timestamps and the time-range cutoff stay lint-pure and consistent.
 */
export function useProviderAudit(slug) {
  const { t } = useTranslation('admin-studio');
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const res = await getProviderAudit(slug);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setRows(validateApiResponse(res, t('common.requestFailed')).body ?? []);
      setNow(Date.now());
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(() => ({ rows, now, isLoading, error }), [rows, now, isLoading, error]);
}
